import { createContext, useCallback, useMemo, useReducer, useRef, useState } from 'react';
import { isDesktop } from 'react-device-detect';
import type { FileRejection } from 'react-dropzone';

import errorLogger from '@picsart/growth-rc/services/errorLogger';
import { ExpandStates } from '@picsart/editorlayout/types';
import { getContext } from 'configs/context';
import noop from '@picsart/rc/noop';

import BatchDataService from 'pages/Batch/sections/Context/BatchDataService';
import pluggableApisService from 'services/pluggableApis';
import type { BATCH_ACTIONS } from 'pages/Batch/configs';
import { ALLOWED_LIMIT_FILES, ASYNC_TOOL_ACTION_MAP, BATCH_ERROR, IMAGE_MIME_TYPES, BATCH_TOOLS } from 'pages/Batch/configs';
import { CollapseStates } from '@picsart/design-system/components/EditorSlot';
import toolsConfig from 'pages/Batch/configs/toolsConfig';
import Toolchain from 'pages/Batch/utils/process/toolchain';
import { updateObjectUrl, prepareFilesForBatch, getResizedFilesForBatch, sanitisePersistedItems } from 'pages/Batch/utils';
import type { ItemSourceEnum } from 'pages/Batch/utils/analytics';
import analytics, { OriginEnum } from 'pages/Batch/utils/analytics';
import useUploadNotification from 'pages/Batch/utils/useUploadNotification';
import watermarkGenerator from 'pages/Batch/utils/watermark/watermarkGenerator';
import loadingImage from 'utils/images/loadingImage';
import type { IResizeApplyAttrs, IWatermarkApplyAttrs, IWatermarkAttrs, TApplyBackgroundProp } from 'pages/Batch/types';
import type { IBatchFileData, ICropApplyAttrs, IEnhanceApplyAttrs, BottomSheetConfig, StatusIndicatorMode } from 'types/store';

import { presetSessionRecorder } from 'services/presets/presetSessionRecorder';
import { getState, dispatch } from 'store/external';
import batchSlice from 'store/batch';

import { BatchDataStatus, INITIAL_EXPORT_INFO, isDev } from 'configs/constants';

import useMount from 'hooks/common/useMount';

import type { IBatchData, IUserBackground } from 'types';

import type { ConfirmModalParams, FileExportInfo, IBatchAttrs, IBatchContext, IBatchContextProviderProps } from './types';
import { ActionTypes } from './types';
import dataReducer from './dataReducer';
import type { IWfxAttrs } from '../Sidebar/WfxTool/types';
import type { IAdjustAttrs } from '../Sidebar/AdjustTool/types';
import type { IComposedEnhanceAttrs } from '../Sidebar/AIEnhanceTool/types';

const defaultValue = {
  data: [],
  process: async () => noop(),
  processItems: async () => noop(),
  recordStep: noop,
  userBackgrounds: [],
  addUserBackground: noop,
  dispatchData: noop,
  selectedKeys: [],
  selectedData: [],
  setSelectedKeys: noop,
  toolWorkingKeys: [],
  workingData: [],
  isSuccessModal: false,
  setIsSuccessModal: noop,
  isZipFile: false,
  setIsZipFile: noop,
  discardPending: noop,
  getProcessingEpoch: () => 0,
  retryItem: async () => {},
  allSelected: true,
  uploadedData: {
    count: 0,
    status: BatchDataStatus.DONE,
  },
  setUploadedData: noop,
  acceptFiles: noop,
  setActiveTool: noop,
  activeTool: BATCH_TOOLS.upload,
  setRemovedType: noop,
  removedType: null,
  setMultiplyFactor: noop,
  isMobile: true,
  expandedState: ExpandStates.Default,
  setExpandState: noop,
  isLoading: false,
  setIsLoading: noop,
  isRestoring: true,
  multiplyFactor: null,
  resizeParams: {
    width: 0,
    height: 0,
    usePadding: true,
    isOriginal: true,
  },
  resetResizeParams: noop,
  bottomSheetConfig: { behavior: 'floating' as const, collapseStates: { normal: { height: 300 } }, defaultSnapPoint: CollapseStates.Normal },
  setBottomSheetConfig: noop,
  imageFormat: IMAGE_MIME_TYPES.PNG,
  setImageFormat: noop,
  showDeleteItemConfirmModal: false,
  setConfirmModalParams: noop,
  setShowDeleteItemConfirmModal: noop,
  areImagesResizing: false,
  setIsErrorMessageShown: noop,
  isErrorMessageShown: false,
  initialRenderComplete: false,
  fileExportInfo: INITIAL_EXPORT_INFO,
  setFileExportInfo: noop,
  myFilesFolderUid: '',
  confirmModalParams: { show: false },
  setMyFilesFolderUid: noop,
};

export const BulkContext = createContext<IBatchContext>(defaultValue);

export function BatchContextProvider({ children }: IBatchContextProviderProps) {
  const [isErrorMessageShown, setIsErrorMessageShown] = useState(false);

  const [{ data, resizeParams }, dispatchData] = useReducer(dataReducer, {
    data: defaultValue.data,
    resizeParams: defaultValue.resizeParams,
  });

  const [isMobile, setIsMobile] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isRestoring, setIsRestoring] = useState(true);
  const [expandedState, setExpandState] = useState(ExpandStates.Default);
  const [multiplyFactor, setMultiplyFactor] = useState<number | null>(null);
  const [userBackgrounds, setUserBackgrounds] = useState<IUserBackground[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [isSuccessModal, setIsSuccessModal] = useState(false);
  const [isZipFile, setIsZipFile] = useState(true);
  const [uploadedData, setUploadedData] = useState(defaultValue.uploadedData);
  const [activeTool, setActiveTool] = useState<BATCH_TOOLS | null>(defaultValue.activeTool);
  const [bottomSheetConfig, setBottomSheetConfig] = useState<BottomSheetConfig>(defaultValue.bottomSheetConfig);
  const [imageFormat, setImageFormat] = useState(defaultValue.imageFormat);
  const [showDeleteItemConfirmModal, setShowDeleteItemConfirmModal] = useState(defaultValue.showDeleteItemConfirmModal);
  const [confirmModalParams, setConfirmModalParams] = useState<ConfirmModalParams>({ show: false });
  const [areImagesResizing, setAreImagesResizing] = useState(false);
  const cancelProcessRef = useRef(noop);
  // Every in-flight Toolchain (single + per-item auto-apply). Cancel (discardPending) cancels them all so a
  // late-resolving result is never written after the user leaves/cancels the tool.
  const activeToolchainsRef = useRef<Set<Toolchain>>(new Set());
  // Monotonic processing epoch. discardPending bumps it; non-Toolchain async flows (AI background
  // generation, previews) capture it at start and skip writing their result if it changed (i.e. canceled).
  const processingEpochRef = useRef(0);
  // Keys with an in-flight per-item auto-apply process — guards against starting a duplicate run when a
  // still-processing item is reselected. (Deselecting never cancels; the process runs to completion.)
  const inFlightItemsRef = useRef<Set<string>>(new Set());
  const isRestoringRef = useRef(true);
  const { showUploaderError, showUploaderWarning } = useUploadNotification();
  const [initialRenderComplete, setInitialRenderComplete] = useState(false);
  const [fileExportInfo, setFileExportInfo] = useState<FileExportInfo>(INITIAL_EXPORT_INFO);

  const [myFilesFolderUid, setMyFilesFolderUid] = useState<string>('');

  // Strict selection: an empty selection yields [] (no "empty = all" fallback), so a tool action never
  // touches a deselected item. The export/save/preset/preview surfaces that want "nothing selected = all"
  // opt into it explicitly via `selectedData.length ? selectedData : data`.
  const selectedData = useMemo(() => data.filter(({ key }) => selectedKeys.includes(key)), [data, selectedKeys]);

  // Sticky per-tool-session working set: the items the active tool operates on. Derived synchronously
  // during render (no effect) — reset to the current selection when the tool changes, otherwise grow with
  // newly-selected keys but never shrink. So deselecting an item inside a tool keeps the changes already
  // applied to it (preview + Apply), while items never selected stay untouched.
  const workingSetRef = useRef<{ tool: BATCH_TOOLS | null; keys: string[] }>({ tool: activeTool, keys: selectedKeys });
  if (workingSetRef.current.tool !== activeTool) {
    workingSetRef.current = { tool: activeTool, keys: selectedKeys };
  } else {
    const missing = selectedKeys.filter(key => !workingSetRef.current.keys.includes(key));
    if (missing.length) {
      workingSetRef.current = { tool: activeTool, keys: [...workingSetRef.current.keys, ...missing] };
    }
  }
  const toolWorkingKeys = workingSetRef.current.keys;

  const workingData = useMemo(() => data.filter(({ key }) => toolWorkingKeys.includes(key)), [data, toolWorkingKeys]);

  const allSelected = useMemo(() => selectedData.length === data.length, [data.length, selectedData]);

  const addUserBackground = useCallback((item: any) => {
    const addedItem: IUserBackground = { url: item };
    setUserBackgrounds(prev => [addedItem, ...prev]);
    return addedItem;
  }, []);

  const handleProcessDone = useCallback(async (item: IBatchData, action: BATCH_ACTIONS) => {
    const updatedItem = item.status === BatchDataStatus.ERROR ? { ...item, lastToolAction: action } : item;

    dispatchData({
      type: ActionTypes.PUT_DATA,
      item: updatedItem,
    });

    errorLogger.leaveBreadcrumb('tool_action', { type: action, process_state: item.status === BatchDataStatus.ERROR ? 'error' : 'done' });
  }, []);

  const recordStep = useCallback((action: BATCH_ACTIONS, attrs: Record<string, any>) => {
    presetSessionRecorder.recordStep(action, attrs, {
      cropTemplate: getState().batch.cropTemplate,
      watermarkGenerator,
    });
  }, []);

  const process = useCallback(
    async (
      tool: BATCH_ACTIONS,
      attrs: IBatchAttrs | ICropApplyAttrs | IEnhanceApplyAttrs | IWatermarkAttrs | IWatermarkApplyAttrs | TApplyBackgroundProp | IResizeApplyAttrs | IWfxAttrs | IAdjustAttrs | IComposedEnhanceAttrs,
      isApply = false,
      // Items to operate on. Defaults to the current selection (empty when nothing is selected), so a tool
      // action never touches a deselected item; working-set tools pass their own apply set explicitly.
      targetData: IBatchData[] = selectedData,
    ) => {
      if (isRestoringRef.current) return;
      if (getState().batch.isToolApplying && !isApply) return;
      const statusIndicatorMode: StatusIndicatorMode = (attrs as Record<string, any>).statusIndicatorMode ?? 'none';

      // Don't reprocess items that are being resumed via polling
      const itemsToProcess = isApply ? targetData : targetData.filter(item => !item.asyncJob);
      if (itemsToProcess.length === 0) return;

      dispatchData({
        type: ActionTypes.UPDATE_STATUSES,
        status: BatchDataStatus.IN_PROGRESS,
        skipItemsWithError: isApply,
        statusIndicatorMode,
        itemKeys: isApply ? undefined : itemsToProcess.map(i => i.key),
      });

      errorLogger.leaveBreadcrumb('tool_action', { type: tool, ...attrs, process_state: 'start' });

      const { getProcess, usePending, useEnhanced, saveAsEnhanced } = toolsConfig[tool];
      const processFn = getProcess(attrs);
      cancelProcessRef.current();
      const toolchain = new Toolchain();
      cancelProcessRef.current = toolchain.cancel.bind(toolchain);
      activeToolchainsRef.current.add(toolchain);

      try {
        if (!areImagesResizing) {
          await toolchain.process({
            callback: processData => handleProcessDone({ ...processData, statusIndicatorMode }, tool),
            usePending,
            useEnhanced,
            saveAsEnhanced,
            processFn,
            srcArray: itemsToProcess,
          });
        }
      } finally {
        activeToolchainsRef.current.delete(toolchain);
      }

      if (isApply) {
        recordStep(tool, attrs as Record<string, any>);
      }
    },
    [handleProcessDone, selectedData, areImagesResizing, recordStep],
  );

  // Auto-apply per-item processing: run a dedicated Toolchain per key so a newly-selected item starts
  // on its own. Skips items that already have a result or are already in-flight (so reselecting an item
  // never starts a duplicate run). Processing is never cancelled on deselect — it runs to completion and
  // its result is kept (committed on Apply regardless of selection).
  const processItems = useCallback(
    async (tool: BATCH_ACTIONS, attrs: IBatchAttrs | ICropApplyAttrs | IEnhanceApplyAttrs | IWatermarkAttrs | IWfxAttrs | IAdjustAttrs | IComposedEnhanceAttrs, keys: string[]) => {
      if (isRestoringRef.current) return;
      const statusIndicatorMode: StatusIndicatorMode = (attrs as Record<string, any>).statusIndicatorMode ?? 'none';

      const targets = data.filter(item => keys.includes(item.key) && !item.pendingResult && !item.asyncJob && !inFlightItemsRef.current.has(item.key));
      if (targets.length === 0) return;

      const { getProcess, usePending, useEnhanced, saveAsEnhanced } = toolsConfig[tool];
      const processFn = getProcess(attrs);

      dispatchData({
        type: ActionTypes.UPDATE_STATUSES,
        status: BatchDataStatus.IN_PROGRESS,
        skipItemsWithError: false,
        statusIndicatorMode,
        itemKeys: targets.map(i => i.key),
      });

      errorLogger.leaveBreadcrumb('tool_action', { type: tool, ...attrs, process_state: 'start', per_item: true });

      await Promise.all(
        targets.map(item => {
          inFlightItemsRef.current.add(item.key);
          const toolchain = new Toolchain();
          activeToolchainsRef.current.add(toolchain);
          return toolchain
            .processItem({
              data: item,
              callback: processData => handleProcessDone({ ...processData, statusIndicatorMode }, tool),
              usePending,
              useEnhanced,
              saveAsEnhanced,
              processFn,
            })
            .finally(() => {
              inFlightItemsRef.current.delete(item.key);
              activeToolchainsRef.current.delete(toolchain);
            });
        }),
      );
    },
    [data, handleProcessDone],
  );

  // Snapshot of the current processing epoch, captured by a non-Toolchain async flow at its start. The flow
  // calls this again before writing its result; a different value means discardPending (Cancel) ran meanwhile.
  const getProcessingEpoch = useCallback(() => processingEpochRef.current, []);

  const discardPending = useCallback(() => {
    cancelProcessRef.current();
    // Cancel every in-flight toolchain (incl. per-item auto-apply) so a result that resolves after Cancel is
    // never written. A canceled Toolchain skips its callback, so no pendingResult lands post-discard.
    activeToolchainsRef.current.forEach(toolchain => toolchain.cancel());
    activeToolchainsRef.current.clear();
    // Invalidate in-flight non-Toolchain async flows (AI background generation/previews) so their late
    // results are dropped instead of being written onto the items after Cancel.
    processingEpochRef.current += 1;
    // Allow re-entering the tool to reprocess these items (their canceled runs will never write back).
    inFlightItemsRef.current.clear();
    dispatchData({ type: ActionTypes.DISCARD_PENDING });
  }, []);

  const retryItem = useCallback(
    async (itemKey: string) => {
      const item = data.find(d => d.key === itemKey);
      if (!item || item.status !== BatchDataStatus.ERROR || !item.lastToolAction) return;

      const batchAction = item.lastToolAction as BATCH_ACTIONS;
      if (!toolsConfig[batchAction]) return;

      dispatchData({
        type: ActionTypes.UPDATE_STATUSES,
        status: BatchDataStatus.IN_PROGRESS,
        statusIndicatorMode: 'both',
        skipItemsWithError: false,
        itemKeys: [itemKey],
      });

      const { getProcess, usePending, useEnhanced, saveAsEnhanced } = toolsConfig[batchAction];
      const processFn = getProcess({});
      const toolchain = new Toolchain();

      await toolchain.process({
        callback: processData => handleProcessDone({ ...processData, statusIndicatorMode: 'both' }, batchAction),
        usePending,
        useEnhanced,
        saveAsEnhanced,
        processFn,
        srcArray: [{ ...item, status: BatchDataStatus.IN_PROGRESS }],
      });
    },
    [data, handleProcessDone],
  );

  const resetResizeParams = useCallback(() => {
    dispatchData({
      type: ActionTypes.SET_RESIZE_PARAMS,
      params: {
        ...resizeParams,
        ...defaultValue.resizeParams,
      },
    });
  }, [resizeParams]);

  useMount(() => {
    const { onOpen, isIOS, isAndroid } = getContext().handlers;

    const mobile = isDev ? !isDesktop : isIOS() || isAndroid();
    setIsMobile(mobile);
    if (mobile) {
      setInitialRenderComplete(true);
    }

    onOpen(async () => {
      // delete all data after kill/close for Mobile
      if (mobile) {
        BatchDataService.deleteAllData();
        // No edits survive on mobile, so the persisted export gate must reset too.
        dispatch(batchSlice.actions.setHasUsedSubscriptionTool(false));
        isRestoringRef.current = false;
        setIsRestoring(false);
        return;
      }

      try {
        const rawData = (BatchDataService.getAllData() || []).filter(Boolean) as IBatchData[];
        const withBlobs = rawData.map(item => ({
          ...item,
          source: updateObjectUrl(item.source),
          result: updateObjectUrl(item.result),
          pendingResult: item.pendingResult && updateObjectUrl(item.pendingResult),
        }));
        const currentData = await sanitisePersistedItems(withBlobs);

        if (rawData.length) {
          BatchDataService.replaceAllData(currentData);
        }

        if (currentData.length) {
          dispatchData({
            type: ActionTypes.ADD_DATA,
            items: currentData,
          });
        } else {
          // Nothing restored (e.g. expired source URLs) — no edits remain, so the
          // persisted export gate must not linger over an empty canvas.
          dispatch(batchSlice.actions.setHasUsedSubscriptionTool(false));
        }

        // Restore selectedKeys from items that were submitted (have asyncJob)
        const submittedKeys = currentData.filter(item => item.asyncJob).map(item => item.key);
        if (submittedKeys.length) {
          setSelectedKeys(submittedKeys);
        }

        // Derive active tool from items with asyncJob (in-flight or completed-pending)
        const firstAsyncJob = currentData.find(item => item.asyncJob)?.asyncJob;
        if (firstAsyncJob) {
          const mapping = ASYNC_TOOL_ACTION_MAP[firstAsyncJob.toolAction];
          if (mapping) setActiveTool(mapping.tool);
        }

        // Clear asyncJob for items that already completed (have pendingResult) — they don't
        // need resume, and a stale asyncJob would block future process() calls.
        const completedItems = currentData.filter(item => item.asyncJob && item.pendingResult);
        completedItems.forEach(item => {
          item.asyncJob = undefined;
          BatchDataService.updateItem(item.key, { asyncJob: undefined });
        });

        // Resume in-flight backend tasks (skip items that already have pendingResult)
        const itemsToResume = currentData.filter(item => item.asyncJob && !item.pendingResult);
        if (itemsToResume.length) {
          dispatchData({
            type: ActionTypes.UPDATE_STATUSES,
            status: BatchDataStatus.IN_PROGRESS,
            statusIndicatorMode: 'both',
            skipItemsWithError: false,
            itemKeys: itemsToResume.map(i => i.key),
          });

          const resumePromises = itemsToResume.map(item => {
            const { task, version, taskId, useWorkflowAPI } = item.asyncJob!;
            return pluggableApisService
              .resumeTaskPolling({ task, version, taskId, initialTimeout: 500, isCancel: () => false, useWorkflowAPI })
              .then(async result => {
                const src = result.image_url || result.url;
                const loaded = await loadingImage(src);
                dispatchData({
                  type: ActionTypes.PUT_DATA,
                  item: {
                    ...item,
                    status: BatchDataStatus.DONE,
                    pendingResult: { src, width: loaded.naturalWidth, height: loaded.naturalHeight },
                    statusIndicatorMode: 'both',
                  },
                });
              })
              .catch(() => {
                const lastToolAction = item.asyncJob?.toolAction ? ASYNC_TOOL_ACTION_MAP[item.asyncJob.toolAction]?.action : undefined;
                BatchDataService.updateItem(item.key, { asyncJob: undefined, lastToolAction });
                dispatchData({
                  type: ActionTypes.PUT_DATA,
                  item: {
                    ...item,
                    status: BatchDataStatus.ERROR,
                    statusIndicatorMode: 'both',
                    asyncJob: undefined,
                    lastToolAction,
                  },
                });
              });
          });

          Promise.allSettled(resumePromises).then(() => {
            isRestoringRef.current = false;
            setIsRestoring(false);
          });
        } else {
          isRestoringRef.current = false;
          setIsRestoring(false);
        }
      } catch (err) {
        // Restore failed before completion — clear the restoring state so users
        // with no data reach EmptyMain instead of being stuck on the loader.
        console.error('[restore] failed:', err);
        isRestoringRef.current = false;
        setIsRestoring(false);
      }
    });
  });

  const acceptFiles = useCallback(
    async (files: File[], rejectedFiles: FileRejection[], isDrop = false, itemSource?: ItemSourceEnum, callback = noop) => {
      const isRejectedFile = rejectedFiles.length > 0;
      isRejectedFile && showUploaderError(BATCH_ERROR.fileFormat);

      const remaining = Math.max(0, ALLOWED_LIMIT_FILES - data.length);
      const exceedsLimit = files.length > remaining;

      if (!files.length || isRejectedFile) {
        analytics.uploadDone(OriginEnum.batch_editor, '', isRejectedFile ? 'fail' : 'cancel', rejectedFiles.length, false, {
          ...(isRejectedFile && { failure_reason: 'file_format' }),
          ...(itemSource && { item_source: itemSource }),
        });
      }
      if (!files.length) return;

      const allowedFiles = exceedsLimit ? files.slice(0, remaining) : files;

      if (exceedsLimit) {
        showUploaderWarning(BATCH_ERROR.exceededFilesLimit);
      }

      if (!allowedFiles.length) {
        setUploadedData({
          count: files.length,
          status: BatchDataStatus.DONE,
        });
        return;
      }

      const gifType = files.find(file => file.type === 'image/gif');
      if (gifType) {
        setIsErrorMessageShown(true);
      }
      setInitialRenderComplete(false);

      try {
        setUploadedData({
          count: allowedFiles.length,
          status: BatchDataStatus.IN_PROGRESS,
        });

        allowedFiles.forEach((file, index) => {
          const reader = new FileReader();
          reader.onload = () => {
            if (index === allowedFiles.length - 1) {
              setUploadedData({
                count: index + 1,
                status: BatchDataStatus.DONE,
              });
            }
          };
          reader.readAsArrayBuffer(file);
        });

        analytics.uploadDone(OriginEnum.batch_editor, '', exceedsLimit ? 'partial_upload' : 'success', allowedFiles.length, isDrop, {
          ...(itemSource && { item_source: itemSource }),
          ...(exceedsLimit && { failure_reason: 'exceeded_limit' }),
        });

        const items: IBatchFileData[] = await prepareFilesForBatch(allowedFiles);
        dispatchData({ type: ActionTypes.ADD_DATA, items });
        // Select the new items in the same batch they're added (keys are preserved through the async resize),
        // so they render selected immediately instead of flashing deselected until the resize finishes.
        setSelectedKeys(prev => [...items.map(el => el.key), ...prev]);
        setAreImagesResizing(true);
        const resizedItems = await getResizedFilesForBatch(items);
        dispatchData({ type: ActionTypes.UPDATE_IMAGES_DATA, items: resizedItems });
        setAreImagesResizing(false);

        BatchDataService.addMultipleData(resizedItems);

        callback();
      } catch (err) {
        console.error('[acceptFiles] failed:', err);
        showUploaderError(BATCH_ERROR.fileFormat);
      }
    },
    [showUploaderError, showUploaderWarning, data.length],
  );

  const contextValue = useMemo(
    () => ({
      data,
      resizeParams,
      resetResizeParams,
      process,
      processItems,
      recordStep,
      userBackgrounds,
      addUserBackground,
      dispatchData,
      selectedKeys,
      selectedData,
      setSelectedKeys,
      getProcessingEpoch,
      toolWorkingKeys,
      workingData,
      isSuccessModal,
      setIsSuccessModal,
      isZipFile,
      setIsZipFile,
      discardPending,
      retryItem,
      allSelected,
      uploadedData,
      setUploadedData,
      acceptFiles,
      activeTool,
      setActiveTool,
      multiplyFactor,
      setMultiplyFactor,
      expandedState,
      setExpandState,
      isLoading,
      setIsLoading,
      isRestoring,
      isMobile,
      bottomSheetConfig,
      setBottomSheetConfig,
      imageFormat,
      setImageFormat,
      showDeleteItemConfirmModal,
      setShowDeleteItemConfirmModal,
      areImagesResizing,
      isErrorMessageShown,
      setIsErrorMessageShown,
      initialRenderComplete,
      fileExportInfo,
      setFileExportInfo,
      myFilesFolderUid,
      confirmModalParams,
      setConfirmModalParams,
      setMyFilesFolderUid,
    }),
    [
      data,
      resizeParams,
      setShowDeleteItemConfirmModal,
      resetResizeParams,
      process,
      processItems,
      recordStep,
      userBackgrounds,
      addUserBackground,
      selectedKeys,
      selectedData,
      toolWorkingKeys,
      workingData,
      isSuccessModal,
      isZipFile,
      discardPending,
      getProcessingEpoch,
      retryItem,
      allSelected,
      uploadedData,
      acceptFiles,
      activeTool,
      multiplyFactor,
      expandedState,
      isLoading,
      isRestoring,
      isMobile,
      bottomSheetConfig,
      imageFormat,
      showDeleteItemConfirmModal,
      areImagesResizing,
      isErrorMessageShown,
      initialRenderComplete,
      fileExportInfo,
      myFilesFolderUid,
      confirmModalParams,
    ],
  );

  return <BulkContext.Provider value={contextValue}>{children}</BulkContext.Provider>;
}
